import { useState, useCallback, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { useChatUnread } from '@/lib/use-chat-unread';
import { useTranslation } from 'react-i18next';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  badge?: number;
}

/* ------------------------------------------------------------------ */
/*  Icons (inline SVGs so we don’t depend on any icon library)         */
/* ------------------------------------------------------------------ */

function HomeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  )
}

function NewspaperIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 5h13a1 1 0 0 1 1 1v12a2 2 0 0 0 2 2H6a2 2 0 0 1-2-2V5z" />
      <path d="M18 8h2a1 1 0 0 1 1 1v9a2 2 0 0 1-2 2" />
      <line x1="7" y1="9" x2="14" y2="9" />
      <line x1="7" y1="13" x2="14" y2="13" />
      <line x1="7" y1="17" x2="11" y2="17" />
    </svg>
  )
}

function CompassIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <circle cx="12" cy="12" r="10" />
      <path d="m16.2 7.8-2 6.3-6.4 2.1 2-6.3z" />
    </svg>
  );
}

function BookOpenIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
      <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
    </svg>
  );
}

function UserIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function SettingsIcon({ className }: { className?: string }) {  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function ChatIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

function VideoIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" className={className}>
      <polygon points="23 7 16 12 23 17 23 7" />
      <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
    </svg>
  );
}

function LogOutIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" x2="9" y1="12" y2="12" />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <line x1="18" x2="6" y1="6" y2="18" />
      <line x1="6" x2="18" y1="6" y2="18" />
    </svg>
  );
}

/* ── prefers-reduced-motion: live media-query hook ── */
function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState<boolean>(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return reduced;
}

/* ── Leaf visibility phases ──
   'full'      → opacity 1 (initial 5 s after mount / route change)
   'fading'    → slow, calm 10 s linear fade towards the recessive state
   'recessive' → settled at reduced opacity (still clearly visible/clickable) */
type LeafVisibility = 'full' | 'fading' | 'recessive';

const LEAF_FULL_OPACITY = 1;
const LEAF_RECESSIVE_OPACITY = 0.4;
const LEAF_FULL_DURATION_MS = 5000;
const LEAF_FADE_DURATION_MS = 10000;
const LEAF_RESTORE_DURATION_MS = 300;   // hover/focus/touch → back to full
const LEAF_REDIM_DURATION_MS = 1500;    // gentle settle back after interaction

/* Top-level destinations: “back” is meaningless there, so the drawer hides it. */
const TOP_LEVEL_ROUTES = [
  '/home',
  '/discover',
  '/newspaper',
  '/views',
  '/books/mine',
  '/requests',
  '/chat',
  '/profile',
  '/settings',
];

/* ── Leaf Clock: sundial-style rotation ── */
function useLeafClock(): number {
  const [rotation, setRotation] = useState(() => {
    const now = new Date()
    const hours = now.getHours() % 12
    const minutes = now.getMinutes()
    return hours * 30 + minutes * 0.5 // 30° per hour, 0.5° per minute
  })

  useEffect(() => {
    function update() {
      const now = new Date()
      const hours = now.getHours() % 12
      const minutes = now.getMinutes()
      setRotation(hours * 30 + minutes * 0.5)
    }
    update()
    const interval = setInterval(update, 60000) // update every minute
    return () => clearInterval(interval)
  }, [])

  return rotation
}

/* ------------------------------------------------------------------ */
/*  LeafNav component                                                  */
/* ------------------------------------------------------------------ */

export default function LeafNav() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [hasShadow, setHasShadow] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const leafRotation = useLeafClock();
  const reducedMotion = usePrefersReducedMotion();
  const [leafVisibility, setLeafVisibility] = useState<LeafVisibility>('full');
  const [leafInteracting, setLeafInteracting] = useState(false);
  // Shared chat unread sum, refreshed each time the drawer opens.
  const chatUnread = useChatUnread(isOpen);
  const touchEndTimer = useRef<number | null>(null);

  /* ---------- Leaf recessive fade ---------- */

  useEffect(() => {
    if (reducedMotion) {
      // No animation: the leaf simply stays fully visible.
      setLeafVisibility('full');
      return;
    }
    // On mount and on every route change: fully visible for 5 s, then a calm
    // 10 s linear fade into the recessive state.
    setLeafVisibility('full');
    const fadeTimer = window.setTimeout(() => setLeafVisibility('fading'), LEAF_FULL_DURATION_MS);
    const settleTimer = window.setTimeout(
      () => setLeafVisibility('recessive'),
      LEAF_FULL_DURATION_MS + LEAF_FADE_DURATION_MS
    );
    return () => {
      window.clearTimeout(fadeTimer);
      window.clearTimeout(settleTimer);
    };
  }, [location.pathname, reducedMotion]);

  useEffect(() => {
    return () => {
      if (touchEndTimer.current !== null) {
        window.clearTimeout(touchEndTimer.current);
      }
    };
  }, []);

  /* ---------- Derived state ---------- */

  const isActive = useCallback(
    (path: string) => {
      return location.pathname === path;
    },
    [location.pathname]
  );

  /* ---------- Scroll shadow effect ---------- */

  useEffect(() => {
    const onScroll = () => {
      setHasShadow(window.scrollY > 4);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* ---------- Keyboard shortcut (Escape to close) ---------- */

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen]);

  /* ---------- Lock body scroll when drawer is open ---------- */

  useEffect(() => {
    if (isOpen) {
      const original = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = original;
      };
    }
  }, [isOpen]);

  /* ---------- Handlers ---------- */

  const openDrawer = useCallback(() => setIsOpen(true), []);
  const closeDrawer = useCallback(() => setIsOpen(false), []);

  const handleSignOut = useCallback(async () => {
    closeDrawer();
    await signOut();
    navigate('/', { replace: true });
  }, [closeDrawer, navigate, signOut]);

  const handleBack = useCallback(() => {
    closeDrawer();
    navigate(-1);
  }, [closeDrawer, navigate]);

  /* ---------- Leaf interaction handlers (restore full visibility) ---------- */

  const handleLeafInteractStart = useCallback(() => {
    if (touchEndTimer.current !== null) {
      window.clearTimeout(touchEndTimer.current);
      touchEndTimer.current = null;
    }
    setLeafInteracting(true);
  }, []);

  const handleLeafInteractEnd = useCallback(() => {
    setLeafInteracting(false);
  }, []);

  // Touch has no reliable "leave" — release full visibility shortly after touchend.
  const handleLeafTouchEnd = useCallback(() => {
    if (touchEndTimer.current !== null) {
      window.clearTimeout(touchEndTimer.current);
    }
    touchEndTimer.current = window.setTimeout(() => {
      setLeafInteracting(false);
      touchEndTimer.current = null;
    }, 1500);
  }, []);

  /* ---------- Leaf trigger style ---------- */

  const leafFullyVisible = reducedMotion || leafInteracting || leafVisibility === 'full';
  const leafOpacity = leafFullyVisible ? LEAF_FULL_OPACITY : LEAF_RECESSIVE_OPACITY;
  const leafOpacityDuration = leafInteracting
    ? LEAF_RESTORE_DURATION_MS
    : leafVisibility === 'fading'
      ? LEAF_FADE_DURATION_MS
      : LEAF_REDIM_DURATION_MS;
  const leafOpacityEasing = !leafInteracting && leafVisibility === 'fading' ? 'linear' : 'ease-out';

  const showBack = !TOP_LEVEL_ROUTES.includes(location.pathname);

  /* ---------- Navigation items ---------- */

  const navItems: NavItem[] = [
    { label: t('nav.home'), path: '/home', icon: <HomeIcon className="h-5 w-5" /> },
    { label: t('nav.discover'), path: '/discover', icon: <CompassIcon className="h-5 w-5" /> },
    { label: t('nav.newspaper'), path: '/newspaper', icon: <NewspaperIcon className="h-5 w-5" /> },
    { label: t('nav.cherry_chat'), path: '/chat', icon: <ChatIcon className="h-5 w-5" />, badge: chatUnread },
    { label: t('nav.cherry_views'), path: '/views', icon: <VideoIcon className="h-5 w-5" /> },
    { label: t('nav.my_books'), path: '/books/mine', icon: <BookOpenIcon className="h-5 w-5" /> },
    { label: t('nav.profile'), path: '/profile', icon: <UserIcon className="h-5 w-5" /> },
    { label: t('nav.settings'), path: '/settings', icon: <SettingsIcon className="h-5 w-5" /> },
  ];

  /* ---------- Render ---------- */

  return (
    <>
      {/* ===== FLOATING LEAF TRIGGER (top-left) ===== */}
      <button
        onClick={openDrawer}
        onMouseEnter={handleLeafInteractStart}
        onMouseLeave={handleLeafInteractEnd}
        onFocus={handleLeafInteractStart}
        onBlur={handleLeafInteractEnd}
        onTouchStart={handleLeafInteractStart}
        onTouchEnd={handleLeafTouchEnd}
        className="fixed left-3 top-3 z-[750] flex h-20 w-20 items-center justify-center hover:scale-105 active:scale-95 focus:outline-none"
        style={{
          opacity: leafOpacity,
          transition: `opacity ${leafOpacityDuration}ms ${leafOpacityEasing}, transform 200ms`,
        }}
        aria-label={t('nav.open')}
        type="button"
      >
        <img
          src="/brand/cherry-leaf.svg"
          alt=""
          className="leaf-breathe h-16 w-16 select-none object-contain transition-transform duration-1000 ease-linear"
          draggable={false}
          style={{ transform: `rotate(${leafRotation - 45}deg)` }}
        />
      </button>

      {/* ===== DRAWER OVERLAY ===== */}
      <div
        onClick={closeDrawer}
        className={[
          'fixed inset-0 z-[800] bg-ink/30 backdrop-blur-[2px] transition-opacity duration-300 ease-out',
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none',
        ].join(' ')}
        aria-hidden="true"
      />

      {/* ===== DRAWER ===== */}
      <aside
        className={[
          'fixed left-0 top-0 z-[800] flex h-full w-72 max-w-[85vw] flex-col',
          'bg-parchment-light shadow-xl',
          'transition-transform duration-300 ease-out',
          isOpen ? 'translate-x-0' : '-translate-x-full',
        ].join(' ')}
        role="dialog"
        aria-modal="true"
        aria-label={t('nav.main')}
      >
        {/* Drawer header */}
        <div className="relative flex flex-col items-center border-b border-silver-light/60 px-4 py-6">
          {/* Hero wordmark */}
          <Link
            to="/newspaper"
            onClick={closeDrawer}
            className="flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-green/30 rounded-md"
          >
            <img
              src="/brand/cherry-books-mark.svg"
              alt="Cherry Books"
              className="h-44 w-auto max-w-[320px] object-contain mx-auto block"
              draggable={false}
            />
          </Link>

          {/* Close button */}
          <button
            onClick={closeDrawer}
            className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full text-ink-muted transition-colors duration-150 hover:bg-silver-light/60 hover:text-ink focus:outline-none focus:ring-2 focus:ring-green/30"
            aria-label={t('nav.close')}
            type="button"
          >
            <XIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Quiet back action — hidden on top-level routes where “back” is meaningless */}
        {showBack && (
          <div className="border-b border-silver-light/40 px-3 py-2">
            <button
              onClick={handleBack}
              className="flex items-center gap-1.5 rounded-md px-2 py-1 font-body text-xs text-ink-muted transition-colors duration-150 hover:text-ink focus:outline-none focus:ring-2 focus:ring-green/30"
              type="button"
            >
              <span aria-hidden="true">←</span>
              {t('common.back')}
            </button>
          </div>
        )}

        {/* Navigation items */}
        <nav className="flex-1 overflow-y-auto px-3 py-4" aria-label={t('nav.main_aria')}>
          <ul className="space-y-1">
            {navItems.map((item) => {
              const active = isActive(item.path);
              return (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    onClick={closeDrawer}
                    className={[
                      'flex items-center gap-3 rounded-lg px-3 py-2.5',
                      'font-body text-sm font-medium transition-all duration-150',
                      'focus:outline-none focus:ring-2 focus:ring-green/30',
                      active
                        ? 'bg-green/10 text-green-dark'
                        : 'text-ink hover:bg-silver-light/50 hover:text-ink-light',
                    ].join(' ')}
                    aria-current={active ? 'page' : undefined}
                  >
                    <span
                      className={[
                        'flex h-8 w-8 shrink-0 items-center justify-center rounded-md',
                        active ? 'bg-green/15 text-green-dark' : 'bg-silver-light/50 text-ink-muted',
                      ].join(' ')}
                    >
                      {item.icon}
                    </span>
                    {item.label}
                    {item.badge != null && item.badge > 0 && (
                      <span className="ml-auto flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-green/10 px-1.5 font-body text-[11px] font-medium text-green">
                        {item.badge}
                      </span>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Footer: Sign out */}
        <div className="border-t border-silver-light/60 px-3 py-3">
          <button
            onClick={handleSignOut}
            className={[
              'flex w-full items-center gap-3 rounded-lg px-3 py-2.5',
              'font-body text-sm font-medium text-ink-muted',
              'transition-colors duration-150',
              'hover:bg-terra/5 hover:text-terra',
              'focus:outline-none focus:ring-2 focus:ring-terra/30',
            ].join(' ')}
            type="button"
          >
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-silver-light/50">
              <LogOutIcon className="h-5 w-5" />
            </span>
            {t('settings.sign_out')}
          </button>

          {/* Small brand mark at bottom */}
          <div className="mt-3 flex justify-center opacity-25 grayscale">
            <img
              src="/brand/cherry-leaf-64.png"
              alt=""
              className="leaf-float h-5 w-5 object-contain"
              draggable={false}
            />
          </div>
        </div>
      </aside>
    </>
  );
}

/* Also export as named export for convenience */
export { LeafNav };
